import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  applyPatch, canonicalQuery, countActive, parseParams, viewPatch,
  type ListSchema, type ListValues,
} from '../components/table/listParams';

/**
 * The URL is the list's state (PRD §4.11, T-37). Filters, sort, page and scope
 * live in `?…`, so Back / Forward restore them and a copied URL reproduces the
 * view for a colleague.
 *
 *   const SCHEMA = { q: { kind: 'string' }, status: { kind: 'enum', values: [...] },
 *                    page: { kind: 'int', default: 1, min: 1 } } satisfies ListSchema;
 *   const list = useListParams(SCHEMA, { pageKey: 'page' });
 *   list.values.status            // typed, validated ('' when absent)
 *   list.set({ status: 'open' })  // push: one history entry per discrete change
 *   list.set({ q }, { replace: true })  // typing replaces, so Back is not a keystroke log
 *
 * Defaults stay out of the URL, params the list does not own (`from=home`) are
 * kept, and changing a filter returns to page 1. Keep `schema` a module-level
 * constant.
 */
export interface SetOptions {
  /** Replace the current history entry instead of pushing a new one. */
  replace?: boolean;
  /** Params outside the schema to drop in the same update (e.g. `from`). */
  drop?: readonly string[];
}

export function useListParams<S extends ListSchema>(schema: S, options: { pageKey?: keyof S & string } = {}) {
  const [searchParams, setSearchParams] = useSearchParams();
  const { pageKey } = options;
  const values = useMemo(() => parseParams(schema, searchParams), [schema, searchParams]);

  // One router update per call: react-router's functional updater reads the
  // params of the last render, so two calls in one tick would lose the first.
  const set = useCallback((patch: Partial<ListValues<S>>, opts: SetOptions = {}) => {
    setSearchParams((prev) => {
      const next = applyPatch(schema, prev, patch, { pageKey });
      opts.drop?.forEach((k) => next.delete(k));
      return next;
    }, { replace: opts.replace });
  }, [schema, pageKey, setSearchParams]);

  /** Any other change, still as one update (e.g. consume a one-shot flag and fill a default). */
  const update = useCallback((fn: (prev: URLSearchParams) => URLSearchParams, opts: { replace?: boolean } = {}) => {
    setSearchParams((prev) => fn(new URLSearchParams(prev)), { replace: opts.replace });
  }, [setSearchParams]);

  /** Drop params the schema does not own (one-shot flags such as `create=true`, `from=home`). */
  const remove = useCallback((keys: readonly string[], opts: SetOptions = {}) => {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      keys.forEach((k) => next.delete(k));
      return next;
    }, { replace: opts.replace });
  }, [setSearchParams]);

  /** Apply a saved / system view: its keys take its values, the other view keys reset. */
  const applyView = useCallback((query: string, viewKeys: readonly (keyof S & string)[], opts: SetOptions = {}) => {
    setSearchParams((prev) => {
      const next = applyPatch(schema, prev, viewPatch(schema, query, viewKeys), { pageKey });
      if (pageKey) next.delete(pageKey);
      opts.drop?.forEach((k) => next.delete(k));
      return next;
    }, { replace: opts.replace });
  }, [schema, pageKey, setSearchParams]);

  const query = useCallback((keys: readonly (keyof S & string)[]) => canonicalQuery(schema, values, keys), [schema, values]);
  const activeCount = useCallback((keys: readonly (keyof S & string)[]) => countActive(schema, values, keys), [schema, values]);

  return { values, set, update, remove, applyView, query, activeCount, searchParams };
}

/**
 * Search text bound to a URL param with a debounce: the field updates on every
 * keystroke, the URL (and the query) 350 ms after typing stops, as a history
 * *replace*. When the URL changes from outside (Back, a view, "ניקוי"), the
 * field follows it.
 */
export function useDebouncedParam(urlValue: string, commit: (value: string) => void, delay = 350): [string, (value: string) => void] {
  const [text, setText] = useState(urlValue);
  const committed = useRef(urlValue);
  const commitRef = useRef(commit);
  commitRef.current = commit;

  // Outside change (Back / Forward, a view, clear): follow the URL.
  useEffect(() => {
    if (urlValue !== committed.current) {
      committed.current = urlValue;
      setText(urlValue);
    }
  }, [urlValue]);

  useEffect(() => {
    const next = text.trim();
    if (next === committed.current) return undefined;
    const timer = window.setTimeout(() => {
      committed.current = next;
      commitRef.current(next);
    }, delay);
    return () => window.clearTimeout(timer);
  }, [text, delay]);

  return [text, setText];
}
